import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { ICryptoSettings } from '../../../../common/models/settings'
import FormGroup from '../../../../core/components/FormGroup'
import {
  getCryptoSettings,
  updateCryptoSettings,
} from '../../../../core/services/festival-settings'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Card, Checkbox } from '@/components/ui'

interface ICryptoModuleValues {
  cryptoPaymentEnabled: boolean
}

export const CryptoModule: React.FC<{ hasEditPermission: boolean }> = ({
  hasEditPermission,
}) => {
  const { editionId, festivalName } = useParams()

  const [cryptoSettings, setCryptoSettings] = useState<ICryptoSettings>()
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const resp = await getCryptoSettings()
        if (resp.status === 200) {
          setCryptoSettings(resp.data)
          setValue('cryptoPaymentEnabled', resp.data.cryptoPaymentEnabled)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const schema = yup.object().shape({
    cryptoPaymentEnabled: yup.boolean().required(),
  })

  const { handleSubmit, setValue, control } = useForm<ICryptoModuleValues>({
    mode: 'onChange',
    resolver: yupResolver<ICryptoModuleValues>(schema),
    defaultValues: {
      cryptoPaymentEnabled: false,
    },
  })

  const submit = async (values: ICryptoModuleValues) => {
    try {
      const { status } = await updateCryptoSettings(values)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async () => {
    if (cryptoSettings) {
      setValue('cryptoPaymentEnabled', cryptoSettings.cryptoPaymentEnabled)
    }
  }

  return (
    <>
      <Card className="p-7.5 bg-gradient-4 border border-monochrome-120">
        <form
          id="settings-crypto-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-[28px]"
        >
          <h3 className="text-title-xs text-monochrome-1 mb-4">Crypto</h3>
          <FormGroup
            label="Enable Pay with Crypto"
            direction="row"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
            labelProps={{
              htmlFor: 'cryptoPaymentEnabled',
            }}
          >
            <Controller
              name="cryptoPaymentEnabled"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="cryptoPaymentEnabled"
                  ref={field.ref}
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                  disabled={!hasEditPermission}
                />
              )}
            />
          </FormGroup>
        </form>

        <div className="flex justify-end gap-2 border-t border-monochrome-120 mt-4 [&>*]:w-[154px] pt-4">
          <Button
            onClick={handleCancel}
            variant="secondary"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            form="settings-crypto-form"
            type="submit"
            variant="primary"
            disabled={!hasEditPermission}
          >
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
